import type { ObjectGraph } from '@jet/environment/dependencies';
import type {
    RouteDefinition,
    RouteImplementation,
    RouteProvider,
    UrlRuleDefinition,
} from '@jet/environment/routing';
import type { URL } from '@jet/environment/util/urls';
import { APP_QUERY_PARAMS } from '../constants';
import { normalizeLocale } from '../dependencies/locale';
import { type RouteableIntent } from '../intents';
import { makeHomePageIntent, type HomePageIntent } from '../intents/HomeIntent';
import { isSignedIn as rawIsSignedIn } from '../dependencies/subscription-status-coordinator';
import type { Intent } from '@jet/environment/dispatching';

type MakeIntent<I> = (options: Omit<I, '$kind'>) => I;

/**
 * App-wide query params, not specific to a particular route, in
 * `UrlRuleDefinition` format. They are all optional.
 */
const APP_QUERY_RULES = APP_QUERY_PARAMS.map((param) => `${param}?`);

/**
 * Make the `routes` function required by `RouteProvider`, to register page
 * intent controllers. Will create `UrlRuleDefinition` variations for optional
 * `{seo}` and/or `{storefront}` path segments, if provided.
 * @see <https://github.pie.apple.com/music/music-ui-js/blob/2022/main/src/utilities/generateRoutes.ts>
 */
function generateRouteDefinition<I extends RouteableIntent>(
    canonicalUrlRule: UrlRuleDefinition,
    makeIntent: MakeIntent<I>,
    routeOptions?: { isLibrary: boolean },
): RouteProvider['routes'] {
    return function routes(objectGraph: ObjectGraph): RouteDefinition[] {
        return [
            {
                rules: permuteUrlRules(canonicalUrlRule),
                handler: routeOptions?.isLibrary
                    ? generateSignInRouteHandler<I>(makeIntent, objectGraph)
                    : generateRouteHandler<I>(makeIntent, objectGraph),
            },
        ];
    };
}

/**
 * Make `UrlRuleDefinition` variations with the optional path segments omitted;
 * i.e. for `/{storefront}/foo/{seo}`, define `/foo/{seo}`, `/{storefront}/foo`
 * and `/foo`.
 */
function permuteUrlRules({
    path: canonicalPath,
    query: canonicalQuery = [],
    ...rest
}: UrlRuleDefinition): UrlRuleDefinition[] {
    const rules = { ...rest, query: [...canonicalQuery, ...APP_QUERY_RULES] };

    // `path` is technically not required in a `UrlRuleDefinition`.
    if (typeof canonicalPath !== 'string') {
        return [rules];
    }

    // De-dupe, in case `canonicalPath` lacks `{seo}` or `{storefront}`.
    const paths = new Set([
        canonicalPath,
        canonicalPath.replace('/{storefront}', ''),
    ]);

    return [...paths].map((path) => ({ path, ...rules }));
}

/**
 * Make the handler function piece of a `RouteDefinition`.
 */
function generateRouteHandler<I extends RouteableIntent>(
    makeIntent: MakeIntent<I>,
    objectGraph: ObjectGraph,
): RouteImplementation {
    return function handler(
        _url: URL,
        handlerParams: Record<string, string>,
    ): I {
        // Rename `l` -> `language`, extract/drop `seo`, and spread the rest.
        const { storefront, l: language, seo, ...intentParams } = handlerParams;

        return makeIntent({
            ...normalizeLocale({ storefront, language }, objectGraph),
            ...intentParams,
            // tslint:disable-next-line:no-object-literal-type-assertion
        } as Omit<I, '$kind'>);
    };
}

function isSignedIn(objectGraph: ObjectGraph) {
    return rawIsSignedIn(objectGraph);
}

function generateSignInRouteHandler<I extends RouteableIntent>(
    makeIntent: MakeIntent<I>,
    objectGraph: ObjectGraph,
): RouteImplementation {
    if (!isSignedIn(objectGraph)) {
        return function handler(
            _url: URL,
            handlerParams: Record<string, string>,
        ): Intent<unknown> {
            // Rename `l` -> `language`, extract/drop `seo`, and spread the rest.
            const { storefront, l: language } = handlerParams;
            return makeHomePageIntent({
                ...normalizeLocale({ storefront, language }, objectGraph),
            }) as unknown as HomePageIntent;
        };
    }

    return generateRouteHandler(makeIntent, objectGraph);
}

export { generateRouteDefinition, generateRouteHandler, permuteUrlRules };
